<template>
  <RevStepper
    v-if="activeStep"
    :active-step="activeStep.name"
    :alternative-text-back="i18n(translations.previousPage)"
    :alternative-text-close="i18n(translations.stepperClose)"
    :alternative-text-completed="i18n(translations.stepperCompleted)"
    :alternative-text-current="i18n(translations.stepperCurrent)"
    has-back
    :steps
    @back="handleStepBack"
  />
</template>

<script setup lang="ts">
import { navigateTo, useHead } from '#imports'
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevStepper } from '@ds/components/Stepper'

import translations from './Stepper.translations'
import type { CheckoutStep } from './Stepper.types'
import BouyguesStepperLogo from './components/BouyguesStepperLogo/BouyguesStepperLogo.vue'

const props = defineProps<{
  hasBouyguesSteps: boolean
  currentStepName: CheckoutStep
  previousStepLink: RouteLocationRaw
}>()

const i18n = useI18n()

const bouyguesSteps = [
  {
    name: 'bouygues',
    label: i18n(translations.bouyguesStep),
    suffix: BouyguesStepperLogo,
  },
]

const steps = computed(() => {
  return [
    {
      name: 'account',
      label: i18n(translations.accountStep),
    },
    {
      name: 'addresses',
      label: i18n(translations.addressesStep),
    },
    ...insertIf(props.hasBouyguesSteps, bouyguesSteps),
    {
      name: 'payment',
      label: i18n(translations.paymentStep),
    },
    {
      name: 'confirmation',
      label: i18n(translations.confirmationStep),
    },
  ]
})

const activeStep = computed(() =>
  steps.value.find((step) => step.name === props.currentStepName),
)

useHead({
  title: computed(() =>
    activeStep.value
      ? `${activeStep.value.label} | ${i18n(translations.pageTitleWithoutPrefix)}`
      : i18n(translations.pageTitle),
  ),
})

const handleStepBack = async () => {
  if (props.previousStepLink) {
    await navigateTo(props.previousStepLink, {
      // Navigating to an external URL is not allowed by default.
      // front-payment app is on another domain
      external: typeof props.previousStepLink === 'string',
    })
  }
}
</script>
